import { MapPin, ClipboardList, Search, ArrowRight } from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { useEnquiry } from "../components/EnquiryModal";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../data/content";

const STEP_ICONS = [ClipboardList, Search, MapPin];

export default function ImportSourcing() {
  const { open } = useEnquiry();
  const { t } = useLanguage();
  const markets = t("markets");
  const steps = t("sourcingPage.steps");

  return (
    <main>
      <PageMeta
        title="Global Food Sourcing & Import UAE | Al Nama Importer Dubai"
        description="Al Nama Importer sources food products and agro commodities from India, Pakistan, Bangladesh, Nepal, Vietnam, California/USA, Afghanistan and other international markets."
        titleAr="التوريد والاستيراد العالمي للأغذية | النعم للاستيراد دبي"
        descriptionAr="تورد النعم للاستيراد المنتجات الغذائية والسلع الزراعية من الهند وباكستان وبنغلاديش ونيبال وفيتنام وكاليفورنيا/الولايات المتحدة وأفغانستان وأسواق دولية أخرى."
      />
      <PageHero
        image={IMAGES.containers}
        overline={t("sourcingPage.heroOver")}
        titleLines={[t("sourcingPage.heroL1"), t("sourcingPage.heroL2")]}
        text={t("sourcingPage.heroText")}
      />

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            overline={t("sourcingPage.marketsOver")}
            title={
              <>
                {t("sourcingPage.marketsTitleA")} <span className="italic text-gold-gradient">{t("sourcingPage.marketsTitleB")}</span>
              </>
            }
            text={t("sourcingPage.marketsText")}
          />
          <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {markets.map((m, i) => (
              <Reveal key={m} delay={(i % 4) * 0.07}>
                <div
                  data-testid={`sourcing-market-${i}`}
                  className="group flex h-full items-center gap-4 rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-6 transition-all duration-500 hover:border-amber-500/45 hover:bg-[#0F1E18]"
                >
                  <span className="font-serif text-2xl italic text-amber-400/40 transition-colors duration-500 group-hover:text-amber-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-display font-medium text-emerald-50">{m}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="h-3 w-3 text-amber-500/60" /> {t("sourcingPage.originTag")}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B1D16] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            overline={t("sourcingPage.howOver")}
            title={
              <>
                {t("sourcingPage.howTitleA")} <span className="italic text-gold-gradient">{t("sourcingPage.howTitleB")}</span>
              </>
            }
          />
          <div className="mt-16 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div data-testid={`sourcing-step-${i}`} className="relative h-full rounded-lg border border-amber-500/15 bg-[#09121F]/60 p-8">
                    <span className="absolute right-6 top-6 font-serif text-4xl italic text-amber-400/25">
                      0{i + 1}
                    </span>
                    <Icon className="h-7 w-7 text-amber-400" strokeWidth={1.4} />
                    <h3 className="mt-6 font-serif text-xl font-semibold text-emerald-50">{s.t}</h3>
                    <p className="mt-3 text-sm font-light leading-relaxed text-slate-300/80">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <button
                data-testid="sourcing-discuss-btn"
                onClick={() => open({ type: "Sourcing Requirement" })}
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-105"
              >
                {t("sourcingPage.cta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
